import { useDebouncedCallback } from "@minervaproject/fds";
import React, { useEffect, useRef, useState } from "react";

type FilterContentType = {
  filterKey: string;
  options: { label: string; value: string }[];
  selected: string[];
  onFilterChange: React.ChangeEventHandler<HTMLInputElement>;
  onClickOnly: React.MouseEventHandler<HTMLAnchorElement>;
  width: number;
  input: string;
};

function FilterContent({
  filterKey,
  options,
  selected,
  onFilterChange,
  onClickOnly,
  width,
  input,
}: FilterContentType) {
  const visibleOptions = options.filter((option) => option.label.match(new RegExp(input, "ig")));
  return (
    <div
      className="p2 bg-white absolute body-s text-black-tint-20"
      style={{
        width: `${width}px`,
        border: "1px solid #dee2e6",
        borderTop: 0,
        boxSizing: "border-box",
        zIndex: 1000,
        maxHeight: "300px",
        overflowY: "scroll",
      }}
    >
      <div className="flex justify-content-between px2">
        <label
          className="form-check-label cursor-pointer"
          style={{ width: "100%" }}
          htmlFor={`${filterKey}-all`}
        >
          All
        </label>
        <input
          type="checkbox"
          className="mt1"
          id={`${filterKey}-all`}
          data-testid={`${filterKey}-all`}
          checked={selected.includes("all")}
          data-value="all"
          onChange={onFilterChange}
        />
      </div>
      <div
        style={{
          height: 0,
          margin: "0.5rem 0",
          overflow: "hidden",
          borderTop: "1px solid #e9ecef",
        }}
      />
      {visibleOptions.map((option) => {
        const subId = option.label.toLowerCase().replaceAll(" ", "-");
        return (
          <div className="flex justify-content-between px2" key={option.label}>
            <label
              className="form-check-label cursor-pointer"
              style={{ width: "100%" }}
              htmlFor={`${filterKey}-${subId}`}
            >
              {option.label}
            </label>
            <div className="flex">
              <a
                href="#"
                className="px2"
                data-value={option.value}
                onClick={onClickOnly}
                id={`${filterKey}-${subId}-only`}
                data-testid={`${filterKey}-${subId}-only`}
              >
                only
              </a>
              <input
                type="checkbox"
                className="mt1"
                id={`${filterKey}-${subId}`}
                data-testid={`${filterKey}-${subId}`}
                checked={selected.includes("all") || selected.includes(option.value)}
                data-value={option.value}
                onChange={onFilterChange}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

type FilterType = {
  title: string;
  filterKey: string;
  width: number;
  options: { label: string; value: string }[];
  selected: string[];
  input: string;
  handleFilterChange: (value: string) => void;
  handleClickOnly: (value: string) => void;
  handleTextInputChange: (value: string) => void;
  clearInput: () => void;
};

function Filter({
  title,
  filterKey,
  width,
  options,
  input,
  selected,
  handleFilterChange,
  handleClickOnly,
  handleTextInputChange,
  clearInput,
}: FilterType) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    } else if (document.activeElement?.id !== `${filterKey}-search`) {
      closeMenu();
    }
  };

  const onFilterChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.dataset.value || "";
    if (value === "all" && !selected.includes("all")) closeMenu();
    handleFilterChange(value);
  };

  const onClickOnly: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) {
      return;
    }
    event.preventDefault();
    const value = event.target.dataset.value || "";
    handleClickOnly(value);
    closeMenu();
  };

  useEffect(() => {
    // called if clicked on outside of element
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) closeMenu();
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  const debouncedOnTextInputChange = useDebouncedCallback((value: string) => {
    handleTextInputChange(value);
  });

  const onTextInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    debouncedOnTextInputChange(value);
  };

  const closeMenu = () => {
    setIsOpen(false);
    const inputElement = document.getElementById(`${filterKey}-search`) as HTMLInputElement;
    if (inputElement.value) {
      inputElement.value = "";
      clearInput();
    }
  };

  let augmentedTitle;
  if (selected.length === 0) augmentedTitle = `${title}: (None)`;
  if (selected.length === 1)
    augmentedTitle = `${title}: ${options.find((option) => option.value === selected[0])?.label}`;
  if (selected.length > 1) augmentedTitle = `${title}: (${selected.length})`;
  if (selected.includes("all")) augmentedTitle = `${title}: (All)`;

  return (
    <div
      ref={wrapperRef}
      className="m2 relative"
      id={`${filterKey}-filter`}
      data-testid={`${filterKey}-filter`}
    >
      <div
        className="flex justify-content-between"
        style={{ width: `${width}px`, border: "1px solid #dee2e6", boxSizing: "border-box" }}
        data-testid={`${filterKey}-search`}
        onClick={onClick}
      >
        <input
          type="text"
          className="p2 border-none bg-white"
          style={{ width: "100%" }}
          id={`${filterKey}-search`}
          placeholder={augmentedTitle}
          onChange={onTextInputChange}
        />
        <div className="p2" data-testid={`${filterKey}-open`}>
          {"\u25bc"}
        </div>
      </div>
      {isOpen ? (
        <FilterContent
          filterKey={filterKey}
          input={input}
          options={options}
          selected={selected}
          onFilterChange={onFilterChange}
          onClickOnly={onClickOnly}
          width={width}
        />
      ) : null}
    </div>
  );
}

export { FilterContent, Filter };
